<template>
  <div class="datetime-entries" :class="{ disabled }">
    <div
      v-for="(entry, index) in normalizedEntries"
      :key="entry.id"
      class="date-entry-card"
      :class="{ clickable: !disabled, hoverable }"
      @click="!disabled && $emit('edit', index)"
    >
      <div class="date-entry-day">{{ getDayNumber(entry.date) }}</div>
      <div class="date-entry-info">
        <span class="date-entry-month">{{ getMonthYear(entry.date) }}</span>
        <span
          v-if="timeMode === 'range' && entry.startTime && entry.endTime"
          class="date-entry-time"
        >
          {{ entry.startTime }} - {{ entry.endTime }}
        </span>
        <span v-else-if="timeMode === 'single' && entry.time" class="date-entry-time">
          {{ entry.time }}
        </span>
      </div>
      <button
        v-if="!disabled && removable"
        class="date-entry-remove"
        type="button"
        @click.stop="$emit('remove', index)"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>

    <!-- Add button (dashed) -->
    <button
      v-if="showAddButton && !disabled"
      type="button"
      class="add-date-btn"
      @click="$emit('add')"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      <span>{{ addLabel }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useLocale } from '@/common/composables/useLocale';
import { parseIsoToLocalDate, parseIsoToLocalTime } from '@/common/utils/dateFormatter';
/**
 * A single date entry to display.
 * Pass either structured objects or ISO strings via `entries`.
 */
export interface DateEntry {
  /** Unique identifier (auto-generated from index when using ISO strings) */
  id?: string;
  /** ISO date string (YYYY-MM-DD) or full ISO datetime */
  date: string;
  /** Start time HH:mm (for range display) */
  startTime?: string;
  /** End time HH:mm (for range display) */
  endTime?: string;
  /** Single time HH:mm */
  time?: string;
}

interface Props {
  /**
   * Array of date entries to display.
   * Accepts either:
   * - `DateEntry[]` (structured objects)
   * - `string[]` (ISO datetime strings — time is extracted automatically)
   */
  entries: (DateEntry | string)[];
  /** Show the remove button on each card */
  removable?: boolean;
  /** Disable interactions */
  disabled?: boolean;
  /** Show the dashed "add" button at the end */
  showAddButton?: boolean;
  /** Label for the add button */
  addLabel?: string;
  /** Enable hover effect on date cards */
  hoverable?: boolean;

  timeMode?: 'range' | 'single' | 'none';
}

const props = withDefaults(defineProps<Props>(), {
  removable: true,
  disabled: false,
  showAddButton: false,
  addLabel: 'Ajouter une date',
  hoverable: false,
});

defineEmits<{
  /** Emitted when clicking a card. Payload: entry index */
  edit: [index: number];
  /** Emitted when clicking the remove button. Payload: entry index */
  remove: [index: number];
  /** Emitted when clicking the add button */
  add: [];
}>();

const { currentLocale } = useLocale();

// ── Locale data ──
const LOCALE_DATA = {
  fr: {
    monthsShort: [
      'JANVIER',
      'FÉVRIER',
      'MARS',
      'AVRIL',
      'MAI',
      'JUIN',
      'JUILLET',
      'AOÛT',
      'SEPTEMBRE',
      'OCTOBRE',
      'NOVEMBRE',
      'DÉCEMBRE',
    ],
  },
  en: {
    monthsShort: [
      'JANUARY',
      'FEBRUARY',
      'MARCH',
      'APRIL',
      'MAY',
      'JUNE',
      'JULY',
      'AUGUST',
      'SEPTEMBER',
      'OCTOBER',
      'NOVEMBER',
      'DECEMBER',
    ],
  },
} as const;

type SupportedLocale = keyof typeof LOCALE_DATA;

const localeData = computed(() => {
  const lang = currentLocale.value?.startsWith('en') ? 'en' : 'fr';
  return LOCALE_DATA[lang as SupportedLocale] ?? LOCALE_DATA.fr;
});

// ── Normalise entries ──
function isoToEntry(iso: string, index: number): DateEntry {
  const dateStr = parseIsoToLocalDate(iso);
  const time = parseIsoToLocalTime(iso);
  if (!dateStr) return { id: `iso-${index}`, date: iso, time: '00:00' };
  return {
    id: `iso-${index}`,
    date: dateStr,
    time: time ?? '00:00',
    startTime: time ?? '00:00',
    endTime: time ?? '00:00',
  };
}

const normalizedEntries = computed<DateEntry[]>(() =>
  props.entries.map((e, i) => {
    if (typeof e === 'string') return isoToEntry(e, i);
    return { ...e, id: e.id ?? `entry-${i}` };
  })
);

function getDayNumber(dateStr: string): string {
  return String(new Date(dateStr).getDate());
}

function getMonthYear(dateStr: string): string {
  const d = new Date(dateStr);
  return `${localeData.value.monthsShort[d.getMonth()]} ${String(d.getFullYear())}`;
}
</script>

<style scoped>
/* ── Entries row ── */
.datetime-entries {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ion-space-3, 12px);
  align-items: stretch;
}
.datetime-entries.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.date-entry-card {
  display: flex;
  align-items: center;
  gap: var(--ion-space-3, 12px);
  padding: var(--ion-space-3, 12px) var(--ion-space-4, 16px);
  background: var(--ion-background-color-primary, #fff);
  border: 1px solid var(--ion-input-border-color, #e0e0e0);
  border-radius: var(--ion-radius-lg, 12px);
  min-width: 180px;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.date-entry-card.clickable {
  cursor: pointer;
}
.date-entry-card.clickable:not(.hoverable) {
  pointer-events: none;
}
.date-entry-card.clickable.hoverable:hover {
  border-color: var(--ion-color-primary, #f5a623);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.date-entry-day {
  font-size: var(--ion-text-2xl, 30px);
  font-weight: var(--ion-font-bold, 700);
  color: var(--ion-text-color, #333);
  line-height: 1;
  min-width: 32px;
  text-align: center;
}

.date-entry-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.date-entry-month {
  font-size: var(--ion-text-xs, 12px);
  font-weight: var(--ion-font-semibold, 600);
  color: var(--ion-text-color, #333);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.date-entry-time {
  font-size: var(--ion-text-xs, 12px);
  color: var(--ion-text-color-secondary, #666);
}

.date-entry-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ion-text-color-secondary, #666);
  padding: var(--ion-space-1, 4px);
  border-radius: var(--ion-radius-sm, 4px);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    color 0.2s,
    background 0.2s;
}
.date-entry-remove:hover {
  color: var(--ion-color-danger, #eb445a);
  background: rgba(235, 68, 90, 0.08);
}

/* ── Add button (dashed) ── */
.add-date-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ion-space-2, 8px);
  padding: var(--ion-space-4, 16px) var(--ion-space-5, 20px);
  border: 2px dashed var(--ion-input-border-color, #d0d0d0);
  border-radius: var(--ion-radius-lg, 12px);
  background: transparent;
  color: var(--ion-text-color-secondary, #666);
  font-size: var(--ion-text-sm, 14px);
  font-weight: var(--ion-font-medium, 500);
  cursor: pointer;
  transition:
    border-color 0.2s,
    color 0.2s,
    background 0.2s;
  min-width: 180px;
  min-height: 64px;
}
.add-date-btn:hover {
  border-color: var(--ion-color-primary, #f5a623);
  color: var(--ion-text-color, #333);
  background: rgba(245, 166, 35, 0.04);
}
</style>
